import React, { useEffect, useState, useRef } from 'react';
import { 
  Box, Button, Typography, Card, CardContent, CardActions, Grid, Chip, 
  Dialog, DialogTitle, DialogContent, TextField, DialogActions,
  IconButton, Badge 
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { supabase } from '../../Supabase/supabaseRealtimeClient';
import { VideoAtendimento, Medico } from '../../types/telemedicina';
import VideoRoom from './VideoRoom';

const TelemedicinaMedico: React.FC = () => {
  const [chamados, setChamados] = useState<VideoAtendimento[]>([]);
  const [activeCall, setActiveCall] = useState<VideoAtendimento | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [medicoId, setMedicoId] = useState<number | null>(null);
  const [prescricao, setPrescricao] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  
  // Notification States
  const [hasNewChamado, setHasNewChamado] = useState(false);
  const notificationIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const subscriptionRef = React.useRef<any>(null);

  useEffect(() => {
    let mounted = true;
    
    const init = async () => {
       await initialize(mounted);
    };
    init();

    return () => {
      mounted = false;
      pararLoopNotificacao();
      if (subscriptionRef.current) {
        console.log('[TelemedicinaMedico] Cleaning up subscription...');
        supabase.removeChannel(subscriptionRef.current);
      }
    };
  }, []);

  // Notification Logic (Ported from AdminPanel)
  const tocarNotificacao = async () => {
    try {
      const audio = new Audio("/assets/notification.mp3");
      audio.volume = 0.7;
      await audio.play();
    } catch (error) {
      console.error("Erro ao tocar áudio:", error);
    }
  };

  const iniciarLoopNotificacao = () => {
    if (notificationIntervalRef.current) clearInterval(notificationIntervalRef.current);
    
    setHasNewChamado(true); // Ativa o Badge
    tocarNotificacao();     // Toca a primeira vez

    // Loop a cada 3 segundos
    notificationIntervalRef.current = setInterval(() => {
      tocarNotificacao();
    }, 3000);
  };

  const pararLoopNotificacao = () => {
    if (notificationIntervalRef.current) {
      clearInterval(notificationIntervalRef.current);
      notificationIntervalRef.current = null;
    }
  };

  const handleNotificationClick = () => {
    pararLoopNotificacao();
    setHasNewChamado(false);
  };

  const initialize = async (isMounted: boolean) => {
    let { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      const accessToken = localStorage.getItem('accessToken');
      const refreshToken = localStorage.getItem('refreshToken');
      
      if (accessToken && refreshToken) {
        const { data, error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken
        });
        if (!error && data.user) {
          user = data.user;
        }
      }
    }

    if (!user || !isMounted) return;
    setUserId(user.id);

    // Get Medico Profile
    const { data: medicoData } = await supabase
      .from('medicos')
      .select('id')
      .eq('user_id', user.id)
      .single();

    if (medicoData && isMounted) {
      setMedicoId(medicoData.id);
      fetchChamados(medicoData.id);
      subscribeToChamados(medicoData.id);
    } else if (isMounted) {
      alert('Perfil de médico não encontrado para este usuário.');
    }
  };

  const subscribeToChamados = (mId: number) => {
    // Evita duplicidade se já houver subscrição ativa
    if (subscriptionRef.current) {
        supabase.removeChannel(subscriptionRef.current);
    }

    // Usar nome de canal único para evitar conflitos
    const channelName = `video_atendimentos_medico_${mId}_${Date.now()}`;
    console.log(`[TelemedicinaMedico] Subscribing to: ${channelName}`);

    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'video_atendimentos' },
        (payload: any) => {
           console.log('[TelemedicinaMedico] Change received!', payload);
           
           // Somente tocar som se for um NOVO encaminhamento para médico
           if (
              payload.eventType === 'UPDATE' && 
              payload.new.status === 'aguardando_medico' && 
              payload.old && payload.old.status !== 'aguardando_medico'
           ) {
              console.log('🚨 Novo paciente na fila! Iniciando loop de notificação.');
              iniciarLoopNotificacao();
           }

           fetchChamados(mId);
        }
      )
      .subscribe((status) => {
        console.log(`[TelemedicinaMedico] Subscription status: ${status}`);
      });
      
    subscriptionRef.current = channel;
  };

  const fetchChamados = async (mId: number) => {
    // Busca chamados aguardando qualquer médico (medico_id null ou meu id)
    // OU chamados em consulta comigo
    const { data } = await supabase
      .from('video_atendimentos')
      .select('*, cliente:cliente(nome, telefone)')
      .or(`status.eq.aguardando_medico,and(status.eq.em_consulta,medico_id.eq.${mId})`)
      // .order('prioridade', { ascending: false }) // Prioridade é varchar, ordenação simples não funciona bem para escala semântica
      .order('data_abertura', { ascending: true });
    
    if (data) {
        // Ordenação manual no cliente para garantir a hierarquia correta de prioridades
        const prioridadeWeight: Record<string, number> = {
            'Emergencia': 5,
            'Muito Urgente': 4,
            'Urgente': 3,
            'Pouco Urgente': 2,
            'Não urgente': 1,
            'normal': 1 // fallback
        };

        const sortedData = data.sort((a, b) => {
            // Se um já estiver em consulta comigo, ele deve ter prioridade máxima (topo)
            const aInConsulta = a.status === 'em_consulta' && a.medico_id === mId ? 1 : 0;
            const bInConsulta = b.status === 'em_consulta' && b.medico_id === mId ? 1 : 0;
            
            if (aInConsulta !== bInConsulta) return bInConsulta - aInConsulta;

            // Ordena por peso da prioridade (maior peso primeiro)
            const weightA = prioridadeWeight[a.prioridade] || 0;
            const weightB = prioridadeWeight[b.prioridade] || 0;

            if (weightA !== weightB) {
                return weightB - weightA;
            }

            // Se empate, o mais antigo primeiro (FIFO) - data_abertura já vem ordenado mas garantimos
            return new Date(a.data_abertura).getTime() - new Date(b.data_abertura).getTime();
        });

        setChamados(sortedData);
    }
  };

  const getPriorityColor = (prioridade: string) => {
      switch (prioridade) {
          case 'Emergencia': return 'error'; // Vermelho
          case 'Muito Urgente': return 'warning'; // Laranja (no MUI warning é laranja/amarelo escuro)
          case 'Urgente': return 'warning'; // Amarelo
          case 'Pouco Urgente': return 'success'; // Verde
          case 'Não urgente': return 'primary'; // Azul
          default: return 'default';
      }
  };

  const handleAtender = async (chamado: VideoAtendimento) => {
    if (!medicoId) return;

    const { data, error } = await supabase
      .from('video_atendimentos')
      .update({ 
        status: 'em_consulta', 
        medico_id: medicoId,
        data_inicio_medico: new Date().toISOString()
      })
      .eq('id', chamado.id)
      .eq('status', 'aguardando_medico') // Safety check
      .select();

    if (!error && data && data.length > 0) {
      setActiveCall({ ...chamado, status: 'em_consulta' });
    } else if (!error && (!data || data.length === 0)) {
       alert('Este paciente já está sendo atendido ou cancelou.');
       if (medicoId) fetchChamados(medicoId);
    } else {
      alert(`Erro ao iniciar consulta: ${error?.message || 'Erro desconhecido'}`);
    }
  };

  const handleOpenFinalizar = () => {
    setOpenDialog(true);
  };

  const handleFinalizarConsulta = async () => {
    if (!activeCall) return;

    const { error } = await supabase
      .from('video_atendimentos')
      .update({
        status: 'finalizado',
        prescricao_medica: prescricao,
        data_fechamento: new Date().toISOString()
      })
      .eq('id', activeCall.id);

    if (!error) {
      setOpenDialog(false);
      setActiveCall(null);
      setPrescricao('');
    } else {
      alert('Erro ao finalizar consulta');
    }
  };

  const handleEndCall = async () => {
    if (!activeCall) return;

    const finalPrescricao = prescricao || 'Encerrado pelo médico via vídeo.';

    const { error } = await supabase
      .from('video_atendimentos')
      .update({
        status: 'finalizado',
        prescricao_medica: finalPrescricao,
        data_fechamento: new Date().toISOString()
      })
      .eq('id', activeCall.id);

    if (!error) {
      setActiveCall(null);
      setPrescricao('');
    } else {
      alert(`Erro ao encerrar: ${error.message}`);
    }
  };

  if (activeCall && userId) {
    return (
      <Box sx={{ height: '90vh', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ flex: 1 }}>
           <VideoRoom 
            roomId={`room-${activeCall.id}`}
            userId={userId} 
            onLeave={() => setActiveCall(null)}
            onEndCall={handleEndCall}
          >
            <Button 
              variant="contained" 
              color="success" 
              fullWidth 
              onClick={handleOpenFinalizar}
              // sx={{ py: 1.5, fontWeight: 'bold' }}
            >
              Finalizar Consulta
            </Button>
          </VideoRoom>
        </Box>

        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>Finalizar Consulta</DialogTitle>
          <DialogContent>
            <Box sx={{ mb: 2, mt: 1 }}>
                <Typography variant="subtitle2">Queixa Inicial:</Typography>
                <Typography variant="body1">{activeCall.queixa_inicial}</Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2">Observações da Triagem:</Typography>
                <Typography variant="body1">{activeCall.observacoes_triagem}</Typography>
            </Box>
            <TextField
              autoFocus
              margin="dense"
              label="Prescrição / Evolução Médica"
              fullWidth
              multiline
              rows={8}
              value={prescricao}
              onChange={(e) => setPrescricao(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
            <Button onClick={handleFinalizarConsulta} variant="contained" color="success">
              Concluir Atendimento
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header com Notificação */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Fila Médica</Typography>
        
        <IconButton 
            color="inherit" 
            onClick={handleNotificationClick}
            aria-label="notificações"
            sx={{ mr: 2 }}
        >
            <Badge color="error" variant="dot" invisible={!hasNewChamado}>
            <NotificationsIcon sx={{ fontSize: 30 }} />
            </Badge>
        </IconButton>
      </Box>

      <Grid container spacing={2}>
        {chamados.map((chamado) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={chamado.id}>
            <Card sx={{ bgcolor: chamado.prioridade === 'Emergencia' ? '#ffebee' : 'white', border: chamado.status === 'em_consulta' ? '2px solid green' : 'none' }}>
              <CardContent>
                <Typography variant="h6">
                  {/* @ts-ignore */}
                  {chamado.cliente?.nome || 'Paciente'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, my: 1, flexWrap: 'wrap' }}>
                    <Chip 
                    label={chamado.status.replace('_', ' ')} 
                    color={chamado.status === 'em_consulta' ? 'success' : 'default'} 
                    size="small" 
                    />
                    {chamado.prioridade && (
                        <Chip 
                            label={chamado.prioridade} 
                            color={getPriorityColor(chamado.prioridade) as any}
                            size="small" 
                            variant={chamado.prioridade === 'Emergencia' ? 'filled' : 'outlined'}
                        />
                    )}
                </Box>
                
                <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 1 }}>Triagem:</Typography>
                <Typography variant="body2" color="text.secondary">
                  {chamado.observacoes_triagem || 'Sem observações'}
                </Typography>
              </CardContent>
              <CardActions>
                {chamado.status === 'aguardando_medico' && (
                  <Button size="small" variant="contained" onClick={() => handleAtender(chamado)}>
                    Atender Paciente
                  </Button>
                )}
                {chamado.status === 'em_consulta' && chamado.medico_id === medicoId && (
                   <Button size="small" variant="contained" color="success" onClick={() => setActiveCall(chamado)}>
                    Retomar Consulta
                  </Button>
                )}
              </CardActions>
            </Card>
          </Grid>
        ))}
        {chamados.length === 0 && (
          <Typography sx={{ p: 2 }}>Nenhum paciente aguardando.</Typography>
        )}
      </Grid>
    </Box>
  );
};

export default TelemedicinaMedico;
